import { ChangeDetectorRef, Component, forwardRef, Input, OnDestroy } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FlatTreeControl } from '@angular/cdk/tree';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
  MatTree,
  MatTreeNodeDef,
  MatTreeNode,
  MatTreeNodePadding,
  MatTreeNodeToggle,
} from '@angular/material/tree';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AllStoreGroupsService } from '@generated/tcea/services/all-store-groups.service';
import { StoreGroupDetailsResponse } from '@generated/tcea/models/store-group-details-response';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { LoadingColor } from '@constants/loading/1.0';
import { MatFormField, MatSuffix, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { NgClass, NgIf, NgFor } from '@angular/common';
import { LoadingComponent } from '../../loading/1.0/loading.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { ButtonComponent } from '../../button/1.0/button.component';

interface StoreGroupFlatNode {
  expandable: boolean;
  name: string;
  level: number;
  id: string;
  selected: boolean;
}

interface StoreGroupNode {
  id: string;
  name: string;
  storeGroups?: StoreGroupNode[];
}

@Component({
  selector: 'txp-store-group-select',
  templateUrl: './store-group-select.component.html',
  styleUrls: ['./store-group-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StoreGroupSelectComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    MatFormField,
    MatInput,
    MatSuffix,
    NgClass,
    MatHint,
    NgIf,
    LoadingComponent,
    NgFor,
    MatCheckbox,
    MatTree,
    MatTreeNodeDef,
    MatTreeNode,
    MatTreeNodePadding,
    ButtonComponent,
    MatTreeNodeToggle,
    TranslateModule,
  ],
})
export class StoreGroupSelectComponent implements OnDestroy, ControlValueAccessor {
  private _brandId: string;
  @Input() set brandId(value: string) {
    if (value && value !== this._brandId) {
      this._brandId = value;
      this.loadStoreGroups();
    }
  }
  get brandId(): string {
    return this._brandId;
  }

  selectedGroupIds: string[] = [];
  allGroups: StoreGroupDetailsResponse[] = [];
  flattenedGroups: StoreGroupDetailsResponse[] = [];
  viewMode: 'list' | 'tree' = 'list';
  isOpen = false;
  isLoading = false;

  buttonStyle = ButtonStyle;
  buttonSize = ButtonSize;
  loadingColor = LoadingColor;

  private unsubscribe$ = new Subject<void>();

  // Tree control logic
  treeControl = new FlatTreeControl<StoreGroupFlatNode>(
    (node) => node.level,
    (node) => node.expandable,
  );

  private _transformer = (node: StoreGroupNode, level: number): StoreGroupFlatNode => {
    return {
      expandable: !!node.storeGroups && node.storeGroups.length > 0,
      name: node.name,
      level: level,
      id: node.id,
      selected: this.selectedGroupIds.includes(node.id),
    };
  };

  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.storeGroups,
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  hasChild = (_: number, node: StoreGroupFlatNode) => node.expandable;

  // ControlValueAccessor
  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(
    private allStoreGroupsService: AllStoreGroupsService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  loadStoreGroups(): void {
    if (!this.brandId) {
      return;
    }
    this.isLoading = true;
    this.allStoreGroupsService
      .getAllStoreGroups({ 'Active-Tenant-Identifier': this.brandId })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res: any) => {
          let rawGroups: StoreGroupDetailsResponse[] = [];
          if (Array.isArray(res)) {
            rawGroups = res;
          } else if (res?._embedded?.storeGroup) {
            rawGroups = res._embedded.storeGroup;
          }

          this.allGroups = rawGroups;
          this.flattenedGroups = this.flattenGroups(rawGroups);
          this.dataSource.data = this.buildTreeNodes(rawGroups);
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  private flattenGroups(groups: StoreGroupDetailsResponse[]): StoreGroupDetailsResponse[] {
    let result: StoreGroupDetailsResponse[] = [];
    groups.forEach((g) => {
      result.push(g);
      if (g.storeGroups?.length) {
        result = result.concat(this.flattenGroups(g.storeGroups));
      }
    });
    return result;
  }

  private buildTreeNodes(groups: StoreGroupDetailsResponse[]): StoreGroupNode[] {
    return groups.map((g) => ({
      id: g.id,
      name: g.name,
      storeGroups: g.storeGroups ? this.buildTreeNodes(g.storeGroups) : [],
    }));
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.viewMode = 'list';
  }

  setViewMode(mode: 'list' | 'tree'): void {
    this.viewMode = mode;
    this.cdr.markForCheck();
  }

  toggleGroup(groupId: string): void {
    if (this.selectedGroupIds.includes(groupId)) {
      this.selectedGroupIds = this.selectedGroupIds.filter((id) => id !== groupId);
    } else {
      this.selectedGroupIds = [...this.selectedGroupIds, groupId];
    }
    this.propagateChange();
  }

  isSelected(groupId: string): boolean {
    return this.selectedGroupIds.includes(groupId);
  }

  getSelectedLabel(): string {
    if (this.selectedGroupIds.length === 0) {
      return '';
    }
    if (this.selectedGroupIds.length === 1) {
      const group = this.flattenedGroups.find((g) => g.id === this.selectedGroupIds[0]);
      return group ? group.name : '';
    }
    return this.translateService.instant('brand.storeGroups.groupsSelected', {
      count: this.selectedGroupIds.length,
    });
  }

  private propagateChange(): void {
    this.onChange(this.selectedGroupIds);
    setTimeout(() => {
      this.cdr.detectChanges();
    });
  }

  // CVA Implementation
  writeValue(value: any): void {
    this.selectedGroupIds = Array.isArray(value) ? value : [];
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
